import type { HrEventMap } from '@squadlab/contracts';
import type { Unsubscribe } from './disposable.js';

/**
 * Typed publish/subscribe bus — the ONLY cross-module channel (ADR 0032).
 *
 * WHY generic over an event map: publishing and subscribing are checked against
 * the versioned `@squadlab/contracts` {@link HrEventMap} (`hr.case.opened.v1`,
 * `hr.leave.balance.updated.v1`, …), so a typo or payload-shape drift is a
 * compile error. `subscribe` returns an {@link Unsubscribe} to honour the
 * Disposable convention.
 *
 * Handlers are isolated: a throwing handler is caught so it cannot break the
 * publisher or sibling handlers (routed to Telemetry at the composition root).
 */

export type { HrEventMap };

export interface EventBus<M> {
  publish<K extends keyof M>(name: K, payload: M[K]): void;
  subscribe<K extends keyof M>(name: K, handler: (payload: M[K]) => void): Unsubscribe;
}

type AnyHandler = (payload: unknown) => void;

export interface EventBusOptions {
  /** Called when a subscriber handler throws, so it can be routed to Telemetry. */
  readonly onHandlerError?: (error: unknown) => void;
}

export function createEventBus<M = HrEventMap>(options: EventBusOptions = {}): EventBus<M> {
  const handlers = new Map<keyof M, Set<AnyHandler>>();

  return {
    publish(name, payload) {
      const set = handlers.get(name);
      if (!set) return;
      // Snapshot so handlers that (un)subscribe during dispatch don't mutate live iteration.
      for (const handler of [...set]) {
        try {
          handler(payload);
        } catch (error) {
          options.onHandlerError?.(error);
        }
      }
    },

    subscribe(name, handler) {
      let set = handlers.get(name);
      if (!set) {
        set = new Set();
        handlers.set(name, set);
      }
      const wrapped = handler as AnyHandler;
      set.add(wrapped);
      return () => {
        set?.delete(wrapped);
      };
    },
  };
}
